import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  ServiceSchema,
  ServicesListResponseSchema,
  paginated,
  type Service,
} from '@repo/api-client/dtos';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';
import { getJSON } from '../internal/http';

type QueryEnableOptions = { enabled?: boolean };

/**
 * List services (a.k.a. catalog items) for the active org via
 * cursor-paginated `useInfiniteQuery`. Pages of 50 are concatenated
 * into a single `items` array. The `hasNextPage` / `fetchNextPage`
 * controls power the settings/services FlatList `onEndReached`.
 */
export const ITEMS_PAGE_SIZE = 50;

export function useItems(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: orgId ? queryKeys.items.all(orgId) : ['items', 'idle'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: String(ITEMS_PAGE_SIZE) });
      if (pageParam) search.set('cursor', pageParam);
      return getJSON(
        http,
        `/orgs/${orgId}/services?${search.toString()}`,
        ServicesListResponseSchema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!orgId,
  });

  const items = useMemo<Service[] | undefined>(
    () =>
      data
        ? (data.pages.flatMap((page) => page.items) as Service[])
        : undefined,
    [data],
  );

  return {
    items,
    nextCursor: data?.pages.at(-1)?.nextCursor,
    loading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage: () => fetchNextPage(),
    isFetchingNextPage,
  };
}

export function useItem(itemId: string, options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data: item,
    refetch,
  } = useQuery({
    queryKey:
      orgId && itemId
        ? queryKeys.items.detail(orgId, itemId)
        : ['items', 'idle'],
    queryFn: () => getJSON(http, `/orgs/${orgId}/services/${itemId}`, ServiceSchema),
    enabled: enabled && !!orgId && !!itemId,
  });

  return { item: item as Service | undefined, loading, error, refetch };
}

export function useItemActivities(itemId: string, options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const ActivitiesSchema = paginated(z.unknown());

  return useQuery({
    queryKey:
      orgId && itemId
        ? queryKeys.items.activities(orgId, itemId)
        : ['items', 'idle'],
    queryFn: async () => {
      if (!orgId || !itemId) return { items: [] };
      return getJSON(http, `/orgs/${orgId}/services/${itemId}/activities`, ActivitiesSchema);
    },
    enabled: enabled && !!orgId && !!itemId,
  });
}

// Legacy aliases kept so screens that still import the older names
// don't break during the v0.1 transition. Stage 6 walks the screens
// and removes them.
export const useServices = useItems;
export const useService = useItem;
export const useServiceActivities = useItemActivities;
